import { Button } from '@coolblue-development/freddy';
import config from '../../../config';
import { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

type ChatDetails = {
  contactId: string;
  participantId: string;
  participantToken: string;
};

type ChatApiResponse = {
  data: {
    startChatResult: ChatDetails;
    sessionType: string;
    region: string;
  };
};

type BearerToken = {
  access_token: string;
};

interface GenerateChatButtonProps {
  isPersistent?: boolean;
}

const GenerateChatButton = ({ isPersistent = false }: GenerateChatButtonProps): JSX.Element => {
  const [loading, setLoading] = useState<boolean>(false);
  const { chatApiAuthUrl, chatApiAuthUsername, chatApiAuthPassword, chatApiUrl } = config;

  const getToken = async (): Promise<BearerToken> => {
    const credentials = btoa(`${chatApiAuthUsername}:${chatApiAuthPassword}`);

    const response = await fetch(chatApiAuthUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Authorization: `Basic ${credentials}`
      },
      body: new URLSearchParams({ grant_type: 'client_credentials' }).toString()
    });

    const data = (await response.json()) as BearerToken;
    if (!response.ok) throw new Error('Failed to fetch token');

    return data;
  };

  const sendChatRequest = async (bearerToken: BearerToken): Promise<ChatDetails> => {
    setLoading(true);
    const chatApiUrlWithId = `${chatApiUrl ?? ''}${uuidv4()}`;
    const SourceContactId = isPersistent ? (localStorage.getItem('contactId') ?? '') : undefined;
    const DisplayName = 'some-name';
    const Locale = 'nl-NL';
    const TouchPoint = 'cato-ui';
    const SubsidiaryId = '1';
    const TouchpointLocation = 'cato';
    const RequestedAt = new Date().toISOString();
    const ChatBotSummary = 'Chat initiated via Cato UI';
    const EstimatedQueueWaitingTime = (Math.random() * 60).toString();

    const response = await fetch(chatApiUrlWithId, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${bearerToken.access_token}`
      },
      body: JSON.stringify({
        displayName: DisplayName,
        locale: Locale,
        touchPoint: TouchPoint,
        intentInteractionId: uuidv4(),
        subsidiaryId: SubsidiaryId,
        touchpointLocation: TouchpointLocation,
        requestedAt: RequestedAt,
        chatBotSummary: ChatBotSummary,
        estimatedQueueWaitingTime: EstimatedQueueWaitingTime,
        ...(isPersistent && { sourceContactId: SourceContactId })
      })
    });

    const data = (await response.json()) as ChatApiResponse;
    if (!response.ok) throw new Error('Failed to fetch chats');

    const { contactId, participantId, participantToken } = data.data.startChatResult;
    if (isPersistent) {
      localStorage.setItem('contactId', contactId);
    }
    return { contactId, participantId, participantToken };
  };

  const connectChatSession = async (chatDetails: ChatDetails): Promise<void> => {
    const region = 'eu-central-1';
    connect.ChatSession.setGlobalConfig({ region: region });

    const customerChatSession = connect.ChatSession.create({
      chatDetails: {
        contactId: chatDetails.contactId,
        participantId: chatDetails.participantId,
        participantToken: chatDetails.participantToken
      },
      type: 'CUSTOMER'
    });

    await customerChatSession.connect();
  };
  const createChat = async (): Promise<void> => {
    setLoading(true);
    try {
      const token = await getToken();
      const requestData = await sendChatRequest(token);
      await connectChatSession(requestData);
    } catch (err) {
      console.error('Failed to connect chat session:', err);
    } finally {
      setLoading(false);
    }
  };
  return (
    <Button variant="primary" onClick={createChat} loading={loading}>
      {isPersistent ? 'Generate Persistent Chat 💬' : 'Generate Chat 💬'}
    </Button>
  );
};

export default GenerateChatButton;
